import { Trans, useLingui } from '@lingui/react/macro';
import { ArrowRight } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';

import { ComponentLocations } from 'src/analytics/trackingTypes';
import { useCheckoutAllowed } from 'src/api/queries';
import { LandingFooter } from 'src/apps/checkout/app/components/LandingFooter';
import { Button, Spinner } from 'src/components';
import { Highlight } from 'src/components/Highlight';
import { ValueProps } from 'src/components/Offers/ValueProps';
import { Price } from 'src/components/Price';
import { PromotionCheckoutLink } from 'src/components/PromotionCheckoutLink';
import { useFreeTrialDuration } from 'src/utils/freeTrialLength';
import { SKU } from 'src/utils/skus';
import { URLS } from 'src/utils/urls/urls';
import { valueProps } from 'src/utils/valueProps';

export function ConnectOfferLandingPage() {
  const { t } = useLingui();
  const checkoutAllowed = useCheckoutAllowed();
  const connectOffer = checkoutAllowed.data?.connectOffer;
  const trialDuration = useFreeTrialDuration(
    connectOffer?.allowed ? connectOffer.freeTrialLengthDays : 0
  );

  if (checkoutAllowed.isPending) {
    return <Spinner />;
  }

  if (checkoutAllowed.data?.connectOffer.allowed) {
    return (
      <>
        <div
          data-cy="connect-offer-landing"
          className="mx-auto max-w-l2xl flex-1 py-32 text-center ls:py-48 lm:py-64 ll:py-96"
        >
          <div className="flex flex-col items-center">
            <Typography
              variant="interface-sm"
              leadingNone
              uppercase
              className="my-0"
            >
              <Trans>Connect subscription</Trans>
            </Typography>

            <Typography
              variant="responsive-heading-xl"
              className="mt-8 mb-24 text-pretty lm:mt-24 lm:mb-40"
            >
              <Trans>
                Start your{' '}
                <Highlight scale={300}>{trialDuration} free</Highlight> trial
              </Trans>
            </Typography>

            <div className="mb-24 text-left lm:mb-48">
              <ValueProps valueProps={valueProps.connect} />
            </div>

            <div className="flex flex-wrap items-center justify-center gap-24">
              <Button
                as="a"
                to={URLS.STORE_CONNECT_OFFER_CHECKOUT}
                variant="primary"
                className="w-full ls:w-fit"
                data-cy="connect-offer-activate-button"
                analytics={{
                  action: 'Activate connect',
                  location:
                    ComponentLocations.CHECKOUT.CONNECT_OFFER_LANDING_MAIN,
                  text: t`Start free trial`,
                }}
              >
                <span>
                  <Trans>Start free trial</Trans>
                </span>{' '}
                <ArrowRight />
              </Button>
              <PromotionCheckoutLink
                componentLocation={
                  ComponentLocations.CHECKOUT.CONNECT_OFFER_LANDING_MAIN
                }
              />
            </div>

            <Typography
              variant="body-sm"
              className="mt-24 max-w-lm text-pretty lm:mt-48"
            >
              <Trans>
                The offer is valid for 30 days after pairing your device.
                Connect costs <Price sku={SKU.connectMonthly} />
                /mo. when your free trial ends. <b>Cancel anytime</b>.
              </Trans>
            </Typography>
          </div>
        </div>
        <LandingFooter
          heading={t`Why activate Connect?`}
          buttonText={t`Try it for free`}
          linkTo={URLS.STORE_CONNECT_OFFER_CHECKOUT}
          componentLocation={
            ComponentLocations.CHECKOUT.CONNECT_OFFER_LANDING_BOTTOM
          }
        />
      </>
    );
  }
}
